import {
  CONVERSATION_STATUS_LABELS,
  type ConversationCounts,
  type ConversationDetail,
  type ConversationRow,
  type ConversationStatus,
  type ConversationsResponse,
  domainFromEmail,
} from '@platform/contracts';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  Dialog,
  DialogDescription,
  DialogTitle,
  EmptyState,
  IconBadge,
  InboxIllustration,
  Input,
  Label,
  SheetContent,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  cn,
} from '@platform/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  BookOpenText,
  Bot,
  CheckCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  ExternalLink,
  HelpCircle,
  Inbox,
  type LucideIcon,
  Lock,
  MailQuestion,
  MessagesSquare,
  PenLine,
  Search,
  UserRoundCheck,
  X,
} from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { toast } from 'sonner';

import { CompanyChip, StoreLogo } from '../components/company';
import { Guard, PageHeader } from '../components/page';
import { ListSkeleton, Loadable } from '../components/states';
import { api, errorMessage } from '../lib/api';
import { fmtDateTime, fmtNum, useScopedQuery } from '../lib/console';
import { fmtAgo } from '../lib/format';
import { useMe } from '../lib/queries';
import { tenantsInScope } from '../lib/scope';
import { useScope } from '../lib/use-scope';
import { TimelineList } from './console';

/**
 * Conversaciones: TODAS las respuestas de los leads, una fila por conversación, con su estado
 * (por responder, derivada, respondida por el asistente, atendida, respuesta automática).
 */

// ------------------------------------------------------------------ estados

type Tab = 'all' | ConversationStatus;

const TABS: { value: Tab; label: string; icon: LucideIcon }[] = [
  { value: 'all', label: 'Todas', icon: Inbox },
  { value: 'pending', label: 'Por responder', icon: Clock3 },
  { value: 'escalated', label: 'Derivadas', icon: MailQuestion },
  { value: 'bot', label: 'Respondidas por el asistente', icon: Bot },
  { value: 'attended', label: 'Atendidas', icon: CheckCheck },
  { value: 'auto', label: 'Automáticas', icon: Clock3 },
];

const STATUS_TONE: Record<
  ConversationStatus,
  'warning' | 'destructive' | 'success' | 'secondary' | 'outline'
> = {
  pending: 'warning',
  escalated: 'destructive',
  bot: 'success',
  attended: 'secondary',
  auto: 'outline',
};

/** Estados que esperan a una persona: llevan la marca lateral en la lista. */
const NEEDS_YOU = new Set<ConversationStatus>(['pending', 'escalated']);

export function StatusBadge({ status }: { status: ConversationStatus }) {
  return <Badge variant={STATUS_TONE[status]}>{CONVERSATION_STATUS_LABELS[status]}</Badge>;
}

const REASON_LABEL: Record<string, string> = {
  model: 'El asistente prefirió que respondiera una persona',
  similarity_gate: 'No encontró la respuesta en la información de tu negocio',
  answerer_error: 'El asistente tuvo un problema técnico',
  validation: 'La respuesta del asistente no pasó la revisión',
  embed_error: 'El asistente tuvo un problema técnico',
  pipeline_error: 'El asistente tuvo un problema técnico',
  send_failed: 'La respuesta del asistente no se pudo enviar',
  bot_paused: 'El asistente estaba en pausa',
  unknown_sender: 'Escribió alguien que no estaba en la campaña',
  vendor_pitch: 'Parece una empresa ofreciéndonos sus servicios',
  lead_closed: 'La conversación ya estaba cerrada por una persona',
};

// ------------------------------------------------------------------ filtros

export interface InboxFilters {
  q: string;
  tenant: string;
  campaign: string;
  from: string;
  to: string;
  includeAuto: boolean;
}

export const EMPTY_INBOX_FILTERS: InboxFilters = {
  q: '',
  tenant: '',
  campaign: '',
  from: '',
  to: '',
  includeAuto: false,
};

const PAGE_SIZE = 25;

/** Filtros + pestaña → querystring de /api/inbox/conversations. Exportado para test. */
export function conversationsQueryString(f: InboxFilters, tab: Tab, page: number): string {
  const p = new URLSearchParams();
  if (tab !== 'all') p.set('status', tab);
  if (f.includeAuto) p.set('includeAuto', 'true');
  for (const k of ['q', 'tenant', 'campaign', 'from', 'to'] as const) {
    const v = f[k].trim();
    if (v) p.set(k, v);
  }
  p.set('page', String(page));
  p.set('pageSize', String(PAGE_SIZE));
  return p.toString();
}

function FilterField({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="flex w-full flex-col gap-1 sm:w-44">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      {children(id)}
    </div>
  );
}

const SELECT_CLS =
  'h-9 rounded-md border border-input bg-card px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

// ------------------------------------------------------------------ lista

function ConversationItem({ c, onOpen }: { c: ConversationRow; onOpen: () => void }) {
  const who = c.company ?? c.leadEmail;
  const needsYou = NEEDS_YOU.has(c.status);
  return (
    <li>
      <Card className="group relative overflow-hidden transition-[box-shadow,border-color] hover:border-highlight/30 hover:shadow-raised has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring">
        {needsYou ? (
          <span
            aria-hidden
            className={cn(
              'absolute inset-y-3 left-0 w-1 rounded-r-full',
              c.status === 'escalated' ? 'bg-destructive' : 'bg-warning',
            )}
          />
        ) : null}
        <CardContent className="flex gap-4 p-4 sm:p-5">
          <StoreLogo
            name={who}
            domain={domainFromEmail(c.leadEmail)}
            size="lg"
            className="hidden sm:inline-flex"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
              <span className="font-display font-semibold">{who}</span>
              {c.company ? (
                <span className="truncate text-muted-foreground">{c.leadEmail}</span>
              ) : null}
              <span
                className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground tabular-nums"
                title={fmtDateTime(c.lastMessageAt)}
              >
                <Clock3 className="size-3" aria-hidden />
                {fmtAgo(c.lastMessageAt)}
              </span>
            </div>
            {c.subject ? (
              <p className="truncate text-xs text-muted-foreground">{c.subject}</p>
            ) : null}
            <p
              className={cn(
                'line-clamp-2 rounded-lg rounded-tl-sm px-3 py-2 text-[0.95rem] leading-relaxed text-pretty',
                c.status === 'auto' ? 'bg-muted/40 text-muted-foreground' : 'bg-muted/60',
              )}
            >
              {c.preview ?? '(sin texto)'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={c.status} />
              <CompanyChip name={c.tenantName} slug={c.tenantSlug} />
              {c.campaignName ? (
                <span className="truncate text-xs text-muted-foreground">{c.campaignName}</span>
              ) : null}
              {c.inboundCount > 1 ? (
                <span className="text-xs text-muted-foreground tabular-nums">
                  · {c.inboundCount} mensajes suyos
                </span>
              ) : null}
              {/* El botón se estira a toda la tarjeta (after:inset-0): toda la fila abre el hilo. */}
              <Button
                size="sm"
                variant={needsYou ? 'default' : 'outline'}
                className="ml-auto after:absolute after:inset-0 after:content-['']"
                onClick={onOpen}
              >
                Ver conversación{' '}
                <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </li>
  );
}

const EMPTY_COPY: Record<Tab, { title: string; description: string }> = {
  all: {
    title: 'Todavía no te ha respondido nadie',
    description:
      'Cuando alguien conteste a una campaña verás aquí la conversación completa, la haya respondido el asistente o no.',
  },
  pending: {
    title: 'No tienes conversaciones por responder',
    description:
      'Aquí aparecen las respuestas a las que todavía no ha contestado nadie: ni tú ni el asistente.',
  },
  escalated: {
    title: 'No hay nada derivado',
    description:
      'Cuando el asistente no sepa contestar algo, te pasará la conversación aquí con un borrador para que respondas tú.',
  },
  bot: {
    title: 'El asistente todavía no ha respondido a nadie',
    description:
      'Si tu empresa tiene el asistente activo, aquí verás las conversaciones que contestó él solo.',
  },
  attended: {
    title: 'Todavía no hay conversaciones atendidas',
    description:
      'Se marcan solas cuando alguien responde desde Instantly o Gmail, o puedes marcarlas tú desde la conversación.',
  },
  auto: {
    title: 'No hay respuestas automáticas',
    description: 'Aquí se juntan los «fuera de la oficina», acuses de recibo y parecidos.',
  },
};

// ------------------------------------------------------------------ detalle

function DetailPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const notesId = useId();
  const [notes, setNotes] = useState('');
  const q = useScopedQuery<ConversationDetail>(
    ['inbox-conversation', id],
    `/api/inbox/conversations/${id}`,
    { requires: 'inbox:read' },
  );
  const attend = useMutation({
    mutationFn: () =>
      api(`/api/inbox/conversations/${id}/attend`, {
        method: 'POST',
        body: notes.trim() ? { notes: notes.trim() } : {},
      }),
    onSuccess: () => {
      toast.success('Conversación marcada como atendida');
      void qc.invalidateQueries({ queryKey: ['console'] });
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const d = q.data;
  const c = d?.conversation;
  const esc = d?.escalation;
  const escPending = esc ? esc.status !== 'handled' : false;
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
      <div className="mb-4 flex items-start gap-3 sm:pr-10">
        <StoreLogo
          name={c?.company ?? c?.leadEmail ?? '·'}
          domain={domainFromEmail(c?.leadEmail)}
          size="lg"
        />
        <div className="min-w-0">
          <DialogTitle className="text-lg font-semibold text-balance break-all">
            {c?.company ?? c?.leadEmail ?? 'Conversación'}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {c
              ? [c.company ? c.leadEmail : null, c.tenantName, c.campaignName]
                  .filter(Boolean)
                  .join(' · ')
              : ''}
          </DialogDescription>
        </div>
      </div>
      <Loadable isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()}>
        {d && c ? (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={c.status} />
              <span className="text-xs text-muted-foreground tabular-nums">
                Último mensaje suyo: {fmtDateTime(c.lastMessageAt)}
              </span>
              <div className="ml-auto flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    void navigator.clipboard.writeText(c.leadEmail).then(() => {
                      toast.success('Email copiado');
                    });
                  }}
                >
                  <Copy /> Copiar email
                </Button>
                {d.uniboxUrl ? (
                  <Button asChild size="sm" variant="outline">
                    <a href={d.uniboxUrl} target="_blank" rel="noopener noreferrer">
                      Responder en Instantly <ExternalLink />
                    </a>
                  </Button>
                ) : null}
              </div>
            </div>
            <section aria-label="Último mensaje">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <MessagesSquare className="size-4 text-highlight" aria-hidden /> Su último mensaje
              </h3>
              <p className="rounded-lg rounded-tl-sm bg-muted p-3 text-sm text-pretty whitespace-pre-line">
                {c.preview ?? '(sin texto)'}
              </p>
            </section>
            {esc ? (
              <>
                <section aria-label="Motivo" className="text-sm">
                  <h3 className="mb-1 flex items-center gap-2 font-semibold">
                    <HelpCircle className="size-4 text-highlight" aria-hidden /> Por qué te la pasó
                    el asistente
                  </h3>
                  <p className="text-muted-foreground">
                    {REASON_LABEL[esc.source ?? ''] ?? 'Necesita a una persona'}
                    {esc.reason ? ` (${esc.reason})` : ''}
                  </p>
                </section>
                {esc.kbMatches.length > 0 ? (
                  <section aria-label="Información relacionada">
                    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                      <BookOpenText className="size-4 text-highlight" aria-hidden /> Lo más parecido
                      que encontró en la información de tu negocio
                    </h3>
                    <ul className="flex flex-col gap-2">
                      {esc.kbMatches.map((m, i) => (
                        <li
                          key={`${String(m.id)}-${String(i)}`}
                          className="rounded-lg border bg-card p-2.5 text-xs leading-relaxed"
                        >
                          {m.preview ?? '(sin vista previa)'}
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                {esc.draft ? (
                  <section aria-label="Respuesta sugerida">
                    <div className="mb-1 flex items-center justify-between">
                      <h3 className="flex items-center gap-2 text-sm font-semibold">
                        <PenLine className="size-4 text-highlight" aria-hidden /> Respuesta sugerida
                        (no se envió)
                      </h3>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          void navigator.clipboard.writeText(esc.draft ?? '').then(() => {
                            toast.success('Respuesta sugerida copiada');
                          });
                        }}
                      >
                        <Copy /> Copiar
                      </Button>
                    </div>
                    <p className="rounded-lg border border-dashed border-highlight/40 bg-accent/40 p-3 text-sm whitespace-pre-line">
                      {esc.draft}
                    </p>
                  </section>
                ) : null}
              </>
            ) : null}
            {c.status === 'attended' ? (
              <Callout tone="success" icon={<CheckCircle2 />} title="Atendida">
                {d.attended
                  ? [
                      d.attended.by ? `Por ${d.attended.by}` : null,
                      fmtDateTime(d.attended.at),
                      d.attended.notes ? `«${d.attended.notes}»` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : esc && !escPending
                    ? `Derivación cerrada${esc.handledBy ? ` por ${esc.handledBy}` : ''}${esc.handledAt ? ` · ${fmtDateTime(esc.handledAt)}` : ''}`
                    : 'Alguien le respondió después de su último mensaje (desde Instantly o Gmail).'}
              </Callout>
            ) : d.writable ? (
              <section aria-label="Marcar atendida" className="flex flex-col gap-2">
                <Label htmlFor={notesId}>Nota (opcional)</Label>
                <Textarea
                  id={notesId}
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                  placeholder="Ej.: le respondí por teléfono y pidió presupuesto"
                  maxLength={2000}
                />
                <Button
                  onClick={() => {
                    attend.mutate();
                  }}
                  disabled={attend.isPending}
                >
                  <UserRoundCheck /> Marcar como atendida
                </Button>
                <p className="text-xs text-muted-foreground text-pretty">
                  {escPending && d.legacyWritesEnabled
                    ? 'También se cierra la derivación del asistente. Si vuelve a escribir, la conversación se abre sola.'
                    : 'Si vuelve a escribir, la conversación se abre sola.'}
                </p>
              </section>
            ) : (
              <Callout tone="info" icon={<Lock />} title="Solo lectura">
                Con tu usuario puedes ver las conversaciones, pero no marcarlas como atendidas.
              </Callout>
            )}
            <section aria-label="Hilo">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <IconBadge size="sm" tone="neutral">
                  <MessagesSquare />
                </IconBadge>
                Conversación completa
              </h3>
              <TimelineList items={d.thread} />
            </section>
          </div>
        ) : null}
      </Loadable>
    </div>
  );
}

// ------------------------------------------------------------------ página

export function InboxPage() {
  const { scope } = useScope();
  const me = useMe();
  const tenants = tenantsInScope(me, scope);
  const [tab, setTab] = useState<Tab>('all');
  const [draft, setDraft] = useState<InboxFilters>(EMPTY_INBOX_FILTERS);
  const [filters, setFilters] = useState<InboxFilters>(EMPTY_INBOX_FILTERS);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const searchId = useId();
  const autoId = useId();
  const qs = conversationsQueryString(filters, tab, page);
  const q = useScopedQuery<ConversationsResponse>(
    ['inbox', 'conversations', qs],
    `/api/inbox/conversations?${qs}`,
    { requires: 'inbox:read', keepPrevious: true },
  );
  const counts: ConversationCounts | undefined = q.data?.counts;
  const rows = q.data?.rows ?? [];
  const pages = q.data ? Math.max(1, Math.ceil(q.data.total / PAGE_SIZE)) : 1;
  const filtered =
    JSON.stringify({ ...filters, includeAuto: false }) !==
    JSON.stringify({ ...EMPTY_INBOX_FILTERS, includeAuto: false });

  const apply = (next: InboxFilters) => {
    setDraft(next);
    setFilters(next);
    setPage(1);
  };
  const set = (k: 'tenant' | 'campaign' | 'from' | 'to') => (v: string) => {
    apply({ ...draft, [k]: v });
  };
  const visibleTabs = TABS.filter((t) => t.value !== 'auto' || filters.includeAuto);
  const tabCount = (t: Tab): number | null => {
    if (!counts) return null;
    if (t === 'all') return filters.includeAuto ? counts.all + counts.auto : counts.all;
    return counts[t];
  };

  return (
    <Guard requires="inbox:read">
      <PageHeader
        title="Conversaciones"
        description="Todas las respuestas de tus contactos, con el hilo completo. Las que esperan a una persona están en «Por responder» y «Derivadas»."
      />
      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4">
          <form
            role="search"
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              apply(draft);
            }}
          >
            <Label htmlFor={searchId} className="sr-only">
              Buscar
            </Label>
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id={searchId}
                type="search"
                placeholder="Tienda, email o texto del mensaje…"
                value={draft.q}
                className="h-10 pl-9"
                onChange={(e) => {
                  setDraft({ ...draft, q: e.target.value });
                }}
              />
            </div>
            <Button type="submit" className="h-10">
              Buscar
            </Button>
          </form>
          <div className="flex flex-wrap items-end gap-3">
            {tenants.length > 1 ? (
              <FilterField label="Empresa">
                {(id) => (
                  <select
                    id={id}
                    value={draft.tenant}
                    onChange={(e) => {
                      set('tenant')(e.target.value);
                    }}
                    className={SELECT_CLS}
                  >
                    <option value="">Todas</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                )}
              </FilterField>
            ) : null}
            <FilterField label="Campaña">
              {(id) => (
                <select
                  id={id}
                  value={draft.campaign}
                  onChange={(e) => {
                    set('campaign')(e.target.value);
                  }}
                  className={SELECT_CLS}
                >
                  <option value="">Todas</option>
                  {(q.data?.campaigns ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({fmtNum(c.count)})
                    </option>
                  ))}
                </select>
              )}
            </FilterField>
            <FilterField label="Desde">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={draft.from}
                  max={draft.to || undefined}
                  className="h-9"
                  onChange={(e) => {
                    set('from')(e.target.value);
                  }}
                />
              )}
            </FilterField>
            <FilterField label="Hasta">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={draft.to}
                  min={draft.from || undefined}
                  className="h-9"
                  onChange={(e) => {
                    set('to')(e.target.value);
                  }}
                />
              )}
            </FilterField>
            <div className="flex h-9 items-center gap-2">
              <Switch
                id={autoId}
                checked={filters.includeAuto}
                onCheckedChange={(v) => {
                  const next = { ...draft, includeAuto: v };
                  apply(next);
                  if (!v && tab === 'auto') setTab('all');
                }}
              />
              <Label htmlFor={autoId} className="text-sm font-normal">
                Mostrar respuestas automáticas
                {counts ? (
                  <span className="text-muted-foreground tabular-nums"> ({counts.auto})</span>
                ) : null}
              </Label>
            </div>
            {filtered ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-9"
                onClick={() => {
                  apply({ ...EMPTY_INBOX_FILTERS, includeAuto: filters.includeAuto });
                }}
              >
                <X /> Limpiar filtros
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v as Tab);
          setPage(1);
        }}
      >
        <TabsList aria-label="Estado de la conversación">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const n = tabCount(t.value);
            return (
              <TabsTrigger key={t.value} value={t.value}>
                <Icon aria-hidden />
                {t.label}
                {n !== null ? (
                  <Badge
                    variant={
                      NEEDS_YOU.has(t.value as ConversationStatus) && n > 0
                        ? 'warning'
                        : 'secondary'
                    }
                    dot={false}
                    className="tabular-nums"
                  >
                    {n}
                  </Badge>
                ) : null}
              </TabsTrigger>
            );
          })}
        </TabsList>
        <TabsContent value={tab}>
          <Loadable
            isLoading={q.isLoading}
            error={q.error}
            onRetry={() => void q.refetch()}
            skeleton={<ListSkeleton rows={6} />}
          >
            {rows.length === 0 ? (
              <EmptyState
                {...(tab === 'all' || tab === 'pending'
                  ? { illustration: <InboxIllustration /> }
                  : { icon: <MessagesSquare /> })}
                title={filtered ? 'No hay conversaciones con esos filtros' : EMPTY_COPY[tab].title}
                description={
                  filtered
                    ? 'Prueba a quitar algún filtro o elige otra empresa arriba.'
                    : EMPTY_COPY[tab].description
                }
              />
            ) : (
              <>
                <p className="mb-2 text-sm text-muted-foreground tabular-nums" aria-live="polite">
                  {fmtNum(q.data?.total ?? 0)}{' '}
                  {q.data?.total === 1 ? 'conversación' : 'conversaciones'}
                  {pages > 1 ? ` · página ${String(page)} de ${String(pages)}` : ''}
                </p>
                <ul className="flex flex-col gap-3" aria-label="Conversaciones">
                  {rows.map((c) => (
                    <ConversationItem
                      key={c.id}
                      c={c}
                      onOpen={() => {
                        setOpen(c.id);
                      }}
                    />
                  ))}
                </ul>
                {pages > 1 ? (
                  <nav aria-label="Paginación" className="mt-3 flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => {
                        setPage((p) => p - 1);
                      }}
                    >
                      <ChevronLeft /> Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= pages}
                      onClick={() => {
                        setPage((p) => p + 1);
                      }}
                    >
                      Siguiente <ChevronRight />
                    </Button>
                  </nav>
                ) : null}
              </>
            )}
          </Loadable>
        </TabsContent>
      </Tabs>
      <Dialog
        open={open !== null}
        onOpenChange={(v) => {
          if (!v) setOpen(null);
        }}
      >
        <SheetContent side="right" className="w-[40rem] max-w-full bg-background p-0">
          {open ? (
            <DetailPanel
              id={open}
              onClose={() => {
                setOpen(null);
              }}
            />
          ) : null}
        </SheetContent>
      </Dialog>
    </Guard>
  );
}
